// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * What the overlap can and cannot do to a score.
 *
 * The specification this tool was built from asked for the headline
 *
 *     "your model scores 91%, but 12% of the eval set leaked;
 *      on the clean subset it is 84%"
 *
 * and that sentence is ARITHMETICALLY IMPOSSIBLE. With an overall mean `o` and
 * a contaminated fraction `c`, the clean-subset mean is pinned to
 *
 *     [ (o - c) / (1 - c),  min(1, o / (1 - c)) ]
 *
 * because the contaminated rows' own mean can only range over [0, 1]. At
 * o = 0.91 and c = 0.12 that interval is [89.77%, 100.00%]: the largest
 * possible fall is 1.23 points, and 91 -> 84 would need c = 43.75%.
 *
 * The upward half is not a technicality either. GPT-3's Appendix C says it
 * outright: "If the clean score is significantly better, our filtering scheme
 * may have preferentially marked easier examples as dirty." So the bound is
 * always printed in both directions, and when the clean subset scores HIGHER
 * that is reported plainly rather than suppressed.
 */

export interface ScoreBound {
  readonly overall: number;
  readonly contaminatedFraction: number;
  readonly low: number;
  readonly high: number;
  /** The largest fall the overlap can account for, in points. */
  readonly maxDrop: number;
}

export function scoreBound(overall: number, contaminatedFraction: number): ScoreBound {
  const c = Math.min(Math.max(contaminatedFraction, 0), 1);
  const o = Math.min(Math.max(overall, 0), 1);
  if (c >= 1) return { overall: o, contaminatedFraction: c, low: 0, high: 1, maxDrop: o };
  const low = Math.max(0, (o - c) / (1 - c));
  const high = Math.min(1, o / (1 - c));
  return { overall: o, contaminatedFraction: c, low, high, maxDrop: Math.max(0, o - low) };
}

export type MetricShape = 'binary' | 'unit-interval' | 'unsupported';

/**
 * Whether the bound may be drawn at all.
 *
 * It is valid only for an UNWEIGHTED MEAN OF PER-ROW SCORES IN [0,1]. A
 * subject-macro-average of the MMLU kind, a weighted metric, or a corpus-level
 * one (BLEU, aggregate F1) is not that, and drawing a bound over it would be a
 * wrong number rather than a missing one.
 */
export function metricShape(scores: readonly number[]): MetricShape {
  if (scores.length === 0) return 'unsupported';
  let binary = true;
  for (const score of scores) {
    if (!Number.isFinite(score) || score < 0 || score > 1) return 'unsupported';
    if (score !== 0 && score !== 1) binary = false;
  }
  return binary ? 'binary' : 'unit-interval';
}

export interface Interval {
  readonly low: number;
  readonly high: number;
  /** How it was computed, so the interface can say. */
  readonly method: 'wilson' | 'normal';
}

/**
 * Wilson score interval, for a proportion of successes.
 *
 * Wilson rather than the normal approximation because the interesting subset is
 * usually small and often at or near 100% — where the normal interval is
 * [1, 1], a claim of certainty from sixty rows.
 */
export function wilson(successes: number, total: number, z = 1.959963985): Interval {
  if (total <= 0) return { low: 0, high: 1, method: 'wilson' };
  const p = successes / total;
  const z2 = z * z;
  const denominator = 1 + z2 / total;
  const centre = p + z2 / (2 * total);
  const spread = z * Math.sqrt((p * (1 - p) + z2 / (4 * total)) / total);
  return {
    low: Math.max(0, (centre - spread) / denominator),
    high: Math.min(1, (centre + spread) / denominator),
    method: 'wilson',
  };
}

/** Normal interval on a mean, for scores that are not 0/1. */
export function normalInterval(scores: readonly number[], z = 1.959963985): Interval {
  const n = scores.length;
  if (n === 0) return { low: 0, high: 1, method: 'normal' };
  const mean = scores.reduce((a, b) => a + b, 0) / n;
  if (n === 1) return { low: mean, high: mean, method: 'normal' };
  let variance = 0;
  for (const score of scores) variance += (score - mean) * (score - mean);
  variance /= n - 1;
  const spread = z * Math.sqrt(variance / n);
  return { low: Math.max(0, mean - spread), high: Math.min(1, mean + spread), method: 'normal' };
}

export function intervalFor(scores: readonly number[], shape: MetricShape): Interval {
  if (shape === 'binary') return wilson(scores.reduce((a, b) => a + b, 0), scores.length);
  return normalInterval(scores);
}

export function mean(scores: readonly number[]): number {
  return scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
}

export interface Comparison {
  readonly shape: MetricShape;
  readonly overall: number;
  readonly cleanMean: number;
  readonly flaggedMean: number;
  readonly cleanCount: number;
  readonly flaggedCount: number;
  readonly cleanInterval: Interval;
  readonly flaggedInterval: Interval;
  readonly bound: ScoreBound;
  /**
   * True when the two intervals overlap, i.e. the data do not separate them.
   * The interface must then suppress the comparison rather than report a
   * difference it cannot support.
   */
  readonly withinNoise: boolean;
}

export function compareSubsets(clean: readonly number[], flagged: readonly number[]): Comparison | null {
  const all = [...clean, ...flagged];
  const shape = metricShape(all);
  if (shape === 'unsupported') return null;

  const overall = mean(all);
  const cleanInterval = intervalFor(clean, shape);
  const flaggedInterval = intervalFor(flagged, shape);
  const withinNoise =
    clean.length === 0 ||
    flagged.length === 0 ||
    (cleanInterval.low <= flaggedInterval.high && flaggedInterval.low <= cleanInterval.high);

  return {
    shape,
    overall,
    cleanMean: mean(clean),
    flaggedMean: mean(flagged),
    cleanCount: clean.length,
    flaggedCount: flagged.length,
    cleanInterval,
    flaggedInterval,
    bound: scoreBound(overall, all.length ? flagged.length / all.length : 0),
    withinNoise,
  };
}

/** Percentage with one decimal, for display. Never rounded to a whole number. */
export function percent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/** Points, for the size of a change. */
export function points(value: number): string {
  return (value * 100).toFixed(1);
}
